/**
 * Shared activity-row labeling, used by both the token page's activity feed
 * and the portfolio Activity/Trading History tabs so operation names and
 * buy/sell tinting read the same everywhere.
 */
export function kcc20ActivityType(source: string): string {
  if (source.includes('mint')) return 'Mint';
  // Unwrap must be checked before wrap — "unwrap" contains "wrap" as a
  // substring, so checking wrap first would misclassify every unwrap.
  // Matches both the compound event names ("unwrap", "withdraw")
  // and the bare on-chain entrypoint name ("unwrap") the backend can send.
  if (
    source.includes('unwrapv3tov2') ||
    source.includes('withdraw') ||
    source.includes('unwrap')
  ) {
    return 'Unwrap';
  }
  if (
    source.includes('wrapv2tov3') ||
    source.includes('deposit') ||
    source.includes('wrap')
  ) {
    return 'Wrap';
  }
  if (source.includes('fill') || source.includes('trade')) return 'Fill';
  if (source.includes('cancel') || source.includes('close')) return 'Close';
  if (source.includes('ask') || source.includes('bid')) return 'Order';
  if (source.includes('transfer')) return 'Transfer';
  return 'Activity';
}

export interface Kcc20ActivitySideInput {
  orderSide?: string | null;
  takerSide?: string | null;
  entrypoint?: string | null;
  kind?: string | null;
}

function normalizedSide(side?: string | null): string {
  return (side || '').toLowerCase();
}

export function kcc20ActivityOrderSide(input: Kcc20ActivitySideInput): string {
  const orderSide = normalizedSide(input.orderSide);
  if (orderSide === 'ask' || orderSide === 'bid' || orderSide === 'cross') {
    return orderSide;
  }

  const source = `${input.entrypoint ?? ''}:${input.kind ?? ''}`.toLowerCase();
  if (source.includes('ask')) return 'ask';
  if (source.includes('bid')) return 'bid';
  return '';
}

export function kcc20ActivityTakerSide(input: Kcc20ActivitySideInput): string {
  const takerSide = normalizedSide(input.takerSide);
  if (takerSide === 'buy' || takerSide === 'sell' || takerSide === 'cross') {
    return takerSide;
  }

  const source = `${input.entrypoint ?? ''}:${input.kind ?? ''}`.toLowerCase();
  const isFill =
    normalizedSide(input.kind) === 'trade' || source.includes('fill');
  if (!isFill) return '';
  if (source.includes('fillask')) return 'buy';
  if (source.includes('fillbid')) return 'sell';
  return '';
}

export function kcc20ActivitySemanticSide(
  type: string,
  input: Kcc20ActivitySideInput,
): string {
  if (type === 'Fill') {
    return kcc20ActivityTakerSide(input) || kcc20ActivityOrderSide(input);
  }
  return kcc20ActivityOrderSide(input) || kcc20ActivityTakerSide(input);
}

/**
 * Maps the normalized activity type + side into standard action labels:
 * Buy / Sell / Mint / Create Buy / Create Sell / Wrap / Unwrap.
 * Falls back to the type itself for anything we don't have explicit copy for.
 */
export function kcc20ActivityAction(type: string, side: string): string {
  const buy = side === 'buy' || side === 'bid';
  const sell = side === 'sell' || side === 'ask';
  switch (type) {
    case 'Mint':
      return 'Mint';
    case 'Wrap':
      return 'Wrap';
    case 'Unwrap':
      return 'Unwrap';
    case 'Fill':
      if (side === 'ask') return 'Buy';
      if (side === 'bid') return 'Sell';
      return buy ? 'Buy' : sell ? 'Sell' : 'Fill';
    case 'Order':
      return buy ? 'Create Buy' : sell ? 'Create Sell' : 'Create Order';
    case 'Close':
      return 'Cancelled order';
    case 'Transfer':
      return 'Transferred';
    default:
      return type;
  }
}

/**
 * Directional tint for an activity row (KCC20-57/60). Rows carrying a
 * buy/bid or sell/ask side — trade fills and order placements alike — carry
 * the red/green buy/sell signal that mirrors the DeFi feed; non-trade
 * operations (mint, wrap, unwrap, transfer) are not directional buys/sells,
 * so they read `info` (blue) instead of borrowing green/red. Metadata-only
 * actions (cancel) stay neutral.
 */
export function kcc20ActivityDirection(
  type: string,
  side: string,
): 'buy' | 'sell' | 'info' | 'neutral' {
  // Non-trade operations move tokens but aren't market buys/sells — keep them
  // off the red/green scale so the directional tint stays meaningful.
  if (
    type === 'Mint' ||
    type === 'Wrap' ||
    type === 'Unwrap' ||
    type === 'Transfer'
  ) {
    return 'info';
  }
  // Cancel/close events can still carry a side (e.g. cancelling an ask/bid),
  // but they move no value, so keep them neutral regardless.
  if (type === 'Close') return 'neutral';
  // For fills, ask/bid names the resting book side. The public activity tape
  // should show the side that took liquidity: taking asks buys, taking bids sells.
  if (type === 'Fill') {
    if (side === 'ask') return 'buy';
    if (side === 'bid') return 'sell';
  }
  if (side === 'buy' || side === 'bid') return 'buy';
  if (side === 'sell' || side === 'ask') return 'sell';
  return 'neutral';
}
